/** Flatten common Firebase / Google API error shapes into one string for matching */
function collectErrorText(error: any): string {
  if (!error) return ''
  const parts: string[] = []
  if (typeof error === 'string') parts.push(error)
  if (error.message) parts.push(String(error.message))
  if (error.code) parts.push(String(error.code))
  if (error?.error?.message) parts.push(String(error.error.message))
  if (error?.response?.data?.error?.message) parts.push(String(error.response.data.error.message))
  if (error?.response?.data?.message) parts.push(String(error.response.data.message))
  try {
    parts.push(JSON.stringify(error))
  } catch {
    /* ignore */
  }
  return parts.join(' ')
}

function getFirebaseErrorByCode(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/too-many-requests': 'Too many failed login attempts.',
    'auth/network-request-failed': 'Network error.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/invalid-verification-code': 'Invalid or expired verification code. Request a new code and try again.',
    'auth/code-expired': 'This code has expired. Request a new code.',
    'auth/invalid-phone-number': 'Invalid phone number.',
    'auth/missing-phone-number': 'Please enter a phone number.',
    'auth/quota-exceeded': 'SMS quota exceeded. Try again later.',
    'auth/captcha-check-failed': 'Security check failed. Refresh the page and try again.',
    'auth/operation-not-allowed':
      'This sign-in method is not allowed. If you are using phone login, ensure SMS is enabled and your region is allowed in Firebase.'
  }

  return errorMessages[errorCode] || 'Authentication failed.'
}

export function getFirebaseErrorMessage(error: any): string {
  const fullText = collectErrorText(error)

  // SMS region policy (Firebase Console → Authentication → Settings → SMS region policy)
  if (
    /OPERATION_NOT_ALLOWED/i.test(fullText) &&
    /SMS/i.test(fullText) &&
    /region/i.test(fullText)
  ) {
    return 'SMS sign-in is not available for your area yet. In Firebase Console, open Authentication → Settings → SMS region policy and enable the countries where users sign in (and ensure Phone sign-in is enabled).'
  }

  if (error?.response?.data?.message?.includes('Firebase:')) {
    const firebaseMessage = error.response.data.message
    const firebaseErrorMatch = firebaseMessage.match(/Firebase: Error \(([^)]+)\)/)
    if (firebaseErrorMatch) {
      return getFirebaseErrorByCode(firebaseErrorMatch[1])
    }
  }

  if (typeof error?.code === 'string' && error.code.startsWith('auth/')) {
    return getFirebaseErrorByCode(error.code)
  }

  if (error?.code && String(error?.message).includes('Firebase:')) {
    return getFirebaseErrorByCode(error.code)
  }

  if (error?.message?.includes('Firebase:')) {
    const firebaseErrorMatch = error.message.match(/Firebase: Error \(([^)]+)\)/)
    if (firebaseErrorMatch) {
      return getFirebaseErrorByCode(firebaseErrorMatch[1])
    }
  }

  return 'Authentication failed. Please check your credentials and try again.'
}
